import { DisplayState } from '../types/display-state.enum';
import {
  SystemHealthComponentDto,
  SystemHealthDetailComponentDto,
  SystemHealthServiceStatusDto,
} from '../types/system-health-api.types';
import { AffectedServiceView, ComponentView } from '../types/system-health.types';

import { toDisplayState } from './to-display-state';

type AnyComponentDto = SystemHealthComponentDto | Partial<SystemHealthDetailComponentDto>;

/** Only the services whose own instance is not fine are worth naming on the row. */
function toAffectedServices(services: SystemHealthServiceStatusDto[] | undefined): AffectedServiceView[] {
  return (services ?? [])
    .map(entry => ({ name: entry.service, state: toDisplayState(entry.status, entry.isStale, entry.lastCheckedAt) }))
    .filter(entry => entry.name && entry.state !== DisplayState.Available);
}

/**
 * Normalises the public and the detail shapes into one view; missing detail fields
 * become empty, and a payload that is not an array yields no rows rather than a throw.
 *
 * @param components - Rows exactly as the gateway delivered them.
 * @returns View models, sorted by label then key.
 */
export function buildComponentViews(components: AnyComponentDto[]): ComponentView[] {
  if (!Array.isArray(components)) return [];

  return components
    .filter((component): component is AnyComponentDto => Boolean(component))
    .map(component => ({
      key: component.key ?? '',
      label: component.label || component.key || '',
      affectedServices: toAffectedServices('services' in component ? component.services : undefined),
      state: toDisplayState(component.status, Boolean(component.isStale), component.lastCheckedAt ?? 0),
      lastCheckedAt: component.lastCheckedAt ?? 0,
      sinceAt: component.lastChangedAt ?? 0,
      error: 'error' in component && component.error ? component.error : '',
    }))
    .sort(byLabelThenKey);
}

function byLabelThenKey(first: ComponentView, second: ComponentView): number {
  return first.label.localeCompare(second.label) || first.key.localeCompare(second.key);
}
